/**
 * Leads inbox query hooks.
 * Uses mock data by default. Replace with real API calls when backend is ready.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { mockLeads } from '@/api/mock'
import { leadsApi } from '@/api/client'
import type { ClaimResponse, LeadListItem } from '@/types'

const USE_MOCK = import.meta.env.VITE_USE_MOCK != null
  ? import.meta.env.VITE_USE_MOCK === 'true'
  : import.meta.env.DEV

// ---- Query keys ----
export const leadsKeys = {
  all: ['leads'] as const,
  list: (filter?: string, channel?: string) => ['leads', 'list', filter, channel] as const,
}

// ---- Hooks ----

export function useLeadsList(filter?: 'all' | 'unclaimed', channel?: string) {
  return useQuery<LeadListItem[]>({
    queryKey: leadsKeys.list(filter, channel),
    queryFn: () => {
      if (USE_MOCK) {
        const items = filter === 'unclaimed'
          ? mockLeads.filter((l) => !l.claimed_by)
          : mockLeads
        return Promise.resolve(items)
      }
      return leadsApi.list({ filter, channel }).then((r) => r.items)
    },
    staleTime: 15_000, // 15 s
    refetchInterval: 30_000, // poll every 30 s (SLA-watcher complement)
  })
}

export function useClaimLead() {
  const qc = useQueryClient()
  // Returns ClaimResponse {ok, claimed_by, claimed_at, message?} — matches leadsApi.claim().
  // On a losing race ok=false + message; callers surface it via a toast.
  return useMutation<ClaimResponse, Error, string>({
    mutationFn: (dealId: string) => {
      if (USE_MOCK) {
        const deal = mockLeads.find((l) => l.deal_id === dealId)
        if (deal && deal.claimed_by) {
          // Already claimed → simulate a losing race so the toast path is exercised.
          return Promise.resolve<ClaimResponse>({
            ok: false,
            claimed_by: deal.claimed_by,
            claimed_at: null,
            message: `Уже взял ${deal.claimed_by_name ?? 'другой оператор'}`,
          })
        }
        if (deal) {
          deal.claimed_by = 'user-rahu-001' // mock current user
          deal.claimed_by_name = 'Раху'
        }
        return Promise.resolve<ClaimResponse>({
          ok: true,
          claimed_by: 'user-rahu-001',
          claimed_at: new Date().toISOString(),
        })
      }
      return leadsApi.claim(dealId)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: leadsKeys.all })
    },
  })
}

export function useReleaseLead() {
  const qc = useQueryClient()
  return useMutation<ClaimResponse, Error, string>({
    mutationFn: (dealId: string) => {
      if (USE_MOCK) {
        const deal = mockLeads.find((l) => l.deal_id === dealId)
        if (deal) {
          deal.claimed_by = null
          deal.claimed_by_name = null
        }
        return Promise.resolve<ClaimResponse>({ ok: true, claimed_by: null, claimed_at: null })
      }
      return leadsApi.release(dealId)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: leadsKeys.all })
    },
  })
}
